"use client"

import React, { useEffect, useState } from "react"
import { useMetaAppDetection } from "../../hooks/useMetaAppDetection"

/**
 * Client-side component to handle Meta app redirects as a fallback
 * This runs after the page loads if the middleware didn't catch it
 */
const MetaAppRedirect: React.FC = () => {
    const {
        isClient,
        shouldRedirect,
        isFacebookApp,
        isInstagramApp,
        openInExternalBrowser,
        copyUrlToClipboard
    } = useMetaAppDetection()

    const [showRedirectModal, setShowRedirectModal] = useState(false)

    useEffect(() => {
        if (!isClient) {
            return
        }

        // Check if user has dismissed this modal before
        const dismissed = localStorage.getItem('meta-app-redirect-dismissed')
        if (dismissed === 'true') {
            return
        }

        if (shouldRedirect) {
            setShowRedirectModal(true)
        }
    }, [isClient, shouldRedirect])

    const handleRedirect = () => {
        openInExternalBrowser()
    }

    const handleDismiss = () => {
        setShowRedirectModal(false)
        // Store dismissal in localStorage to avoid showing again
        localStorage.setItem('meta-app-redirect-dismissed', 'true')
    }

    const handleCopyUrl = async () => {
        try {
            await copyUrlToClipboard()
            alert('Link copied to clipboard! Paste it in your browser.')
        } catch (error) {
            console.error('Failed to copy URL:', error)
            alert('Failed to copy URL. Please manually copy the URL from your address bar.')
        }
    }

    if (!showRedirectModal || !shouldRedirect) {
        return null
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
                <div className="text-6xl mb-4">🌐</div>

                <h2 className="text-xl font-bold text-gray-800 mb-3">
                    Better Experience Available
                </h2>

                <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                    For the best DGbet experience with full functionality,
                    open this link in your default browser instead of the{' '}
                    {isFacebookApp ? 'Facebook' : 'Instagram'} app.
                </p>

                <div className="space-y-3">
                    <button
                        onClick={handleRedirect}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
                    >
                        Open in Browser
                    </button>

                    <button
                        onClick={() => {
                            handleCopyUrl().catch(console.error)
                        }}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                    >
                        Copy Link
                    </button>

                    <button
                        onClick={handleDismiss}
                        className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                    >
                        Continue Here
                    </button>
                </div>

                <p className="text-xs text-gray-500 mt-4">
                    Paste the copied link in any browser for the full experience.
                </p>
            </div>
        </div>
    )
}

export default MetaAppRedirect
